import { useState } from "react";
import { cn } from "~/lib/utils";
import { ProjectLetterAvatar } from "./ProjectLetterAvatar";

export type ProjectIdentityIconVariant = "compact" | "tile";

const VARIANT_CLASS: Readonly<Record<ProjectIdentityIconVariant, string>> = {
  compact: "size-3.5 rounded-sm",
  tile: "size-10 rounded-md border border-border/70 bg-background shadow-sm",
};

/**
 * Canonical project identity renderer shared by CMMD-backed project surfaces.
 * Image provenance wins, then the explicit glyph, then the project-name
 * fallback. Failed remote images degrade locally without mutating CMMD truth.
 */
export function ProjectIdentityIcon(input: {
  readonly iconUrl: string | null | undefined;
  readonly icon: string | null | undefined;
  readonly projectName: string;
  readonly variant?: ProjectIdentityIconVariant;
  readonly className?: string | undefined;
}) {
  const [failedIconUrl, setFailedIconUrl] = useState<string | null>(null);
  const variant = input.variant ?? "compact";
  const variantClass = VARIANT_CLASS[variant];
  const trimmedIconUrl = input.iconUrl?.trim() ?? "";
  const trimmedIcon = input.icon?.trim() ?? "";

  if (trimmedIconUrl !== "" && trimmedIconUrl !== failedIconUrl) {
    return (
      <img
        src={trimmedIconUrl}
        alt=""
        className={cn("shrink-0 object-contain", variantClass, input.className)}
        data-project-icon="cmmd-image"
        onError={() => setFailedIconUrl(trimmedIconUrl)}
      />
    );
  }

  if (trimmedIcon !== "") {
    return (
      <span
        aria-hidden="true"
        data-project-icon="cmmd-emoji"
        className={cn(
          "inline-flex shrink-0 items-center justify-center overflow-hidden font-semibold leading-none",
          variant === "tile" ? "text-base" : "text-[11px]",
          variantClass,
          input.className,
        )}
      >
        <span className={variant === "tile" ? "max-w-8 truncate" : undefined}>{trimmedIcon}</span>
      </span>
    );
  }

  return (
    <ProjectLetterAvatar
      projectName={input.projectName}
      className={cn(
        variantClass,
        variant === "tile" ? "text-base shadow-sm" : undefined,
        input.className,
      )}
    />
  );
}
